import { CrudPage } from '../../components/CrudPage';
import { type HolidayDto, HolidayControllerService } from '../../api';
import HolidayFormDialog from '../forms/HolidayFormDialog';
import { useTranslation } from 'react-i18next';

// Type with required id for the CrudPage
type HolidayWithId = Omit<HolidayDto, 'id'> & { id: string };

const HolidaysPage = () => {
  const { i18n } = useTranslation();

  // Custom renderer for holiday date with locale formatting
  const dateRenderer = (data: HolidayWithId) => {
    const value = data.holidayDate;
    if (!value) return '';
    
    try {
      const date = new Date(value);
      return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    } catch {
      return value;
    }
  };

  return (
    <CrudPage<HolidayWithId>
      titleKey="headers.holidays"
      gridMeta={[
        { field: 'name', headerKey: 'holiday.grid.field.name', width: 150 },
        { 
          field: 'holidayDate', 
          headerKey: 'holiday.grid.field.holidayDate', 
          width: 120,
          render: dateRenderer
        },
        { field: 'description', headerKey: 'holiday.grid.field.description', width: 200 }
      ]}
      fetchData={async () => {
        const holidays = await HolidayControllerService.findAll();
        return holidays.map(holiday => ({
          ...holiday,
          id: holiday.uuid || ''
        }));
      }}
      saveData={async (data: HolidayWithId) => {
        const holidayData: HolidayDto = {
          ...data,
          uuid: data.id // id is the uuid in our internal type
        };
        
        if (data.id) {
          // Update existing holiday
          await HolidayControllerService.update(data.id, holidayData);
        } else {
          // Create new holiday
          await HolidayControllerService.create(holidayData);
        }
      }}
      deleteData={HolidayControllerService.delete}
      FormComponent={HolidayFormDialog}
      entityName="holiday"
    />
  );
};

export default HolidaysPage;
